class ProductCrossSells extends HTMLElement {
  constructor() {
    super();

    this.onClickItem = this.onClickItem.bind(this);
    this.onKeyDownItem = this.onKeyDownItem.bind(this);
    this.updateTotals = this.updateTotals.bind(this);
    this.onClickAddAll = this.onClickAddAll.bind(this);
    this.onClickModalAddToCart = this.onClickModalAddToCart.bind(this);
    this.onClickToggleShowMore = this.onClickToggleShowMore.bind(this);
    this.onModalOpenedResetCrossSellButtons =
      this.onModalOpenedResetCrossSellButtons.bind(this);
    this.onProductVariantChange = this.onProductVariantChange.bind(this);
    this.onCrossSellVariantChange = this.onCrossSellVariantChange.bind(this);
    this.onFavoriteStoreUpdated = this.onFavoriteStoreUpdated.bind(this);
    this.availabilityRequests = new Map();
    this.bundleViewObserver = null;
  }

  connectedCallback() {
    if (this._listenersAttached) return;
    this._listenersAttached = true;

    const items = this.querySelectorAll(".product-cross-sells__item");
    items.forEach((item) => {
      item.addEventListener("click", this.onClickItem);
      item.addEventListener("keydown", this.onKeyDownItem);
    });

    const addAllBtn = this.querySelector(
      ".product-cross-sells__add-all-to-cart-button",
    );
    if (addAllBtn) addAllBtn.addEventListener("click", this.onClickAddAll);

    document.addEventListener("click", this.onClickModalAddToCart);
    document.body.addEventListener(
      "modalOpened",
      this.onModalOpenedResetCrossSellButtons,
    );

    const showMoreBtn = this.querySelector(
      ".product-cross-sells__show-more-button",
    );
    if (showMoreBtn) {
      showMoreBtn.setAttribute("aria-expanded", "false");
      showMoreBtn.addEventListener("click", this.onClickToggleShowMore);
    }

    this.querySelectorAll(".product-cross-sells__item-variant-select").forEach(
      (select) => {
        select.addEventListener("click", (event) => event.stopPropagation());
        select.addEventListener("keydown", (event) => event.stopPropagation());
        select.addEventListener("change", this.onCrossSellVariantChange);
      },
    );

    document.addEventListener("change", this.onProductVariantChange);
    window.addEventListener(
      "favoriteStoreUpdated",
      this.onFavoriteStoreUpdated,
    );

    this.querySelectorAll(".product-cross-sells__item-variant-select").forEach(
      (select) => {
        this.syncCrossSellVariantItem(select);
      },
    );
    this.updateTotals();
    queueMicrotask(() => this.syncCurrentProductBundleItem());
    this.updateAllStoreAvailability();
    this.initBundleViewedTracking();
  }

  disconnectedCallback() {
    if (!this._listenersAttached) return;
    this._listenersAttached = false;

    this.querySelectorAll(".product-cross-sells__item").forEach((item) => {
      item.removeEventListener("click", this.onClickItem);
      item.removeEventListener("keydown", this.onKeyDownItem);
    });

    const addAllBtn = this.querySelector(
      ".product-cross-sells__add-all-to-cart-button",
    );
    if (addAllBtn) addAllBtn.removeEventListener("click", this.onClickAddAll);

    document.removeEventListener("click", this.onClickModalAddToCart);
    document.body.removeEventListener(
      "modalOpened",
      this.onModalOpenedResetCrossSellButtons,
    );

    const showMoreBtn = this.querySelector(
      ".product-cross-sells__show-more-button",
    );
    if (showMoreBtn)
      showMoreBtn.removeEventListener("click", this.onClickToggleShowMore);

    this.querySelectorAll(".product-cross-sells__item-variant-select").forEach(
      (select) => {
        select.removeEventListener("change", this.onCrossSellVariantChange);
      },
    );

    document.removeEventListener("change", this.onProductVariantChange);
    window.removeEventListener(
      "favoriteStoreUpdated",
      this.onFavoriteStoreUpdated,
    );
    this.bundleViewObserver?.disconnect();
    this.bundleViewObserver = null;
  }

  onCrossSellVariantChange(event) {
    this.syncCrossSellVariantItem(event.currentTarget, { selectRow: true });

    const row = event.currentTarget.closest(".product-cross-sells__item");
    if (!row) return;

    this.pushBundleEvent("bundle_variant_changed", {
      item: this.getBundleItemData(row),
      selected_item_count: this.getSelectedBundleItems().length,
    });
  }

  initBundleViewedTracking() {
    if (this._bundleViewedTracked) return;

    const trackViewed = () => {
      if (this._bundleViewedTracked) return;
      this._bundleViewedTracked = true;
      this.bundleViewObserver?.disconnect();
      this.bundleViewObserver = null;
      this.pushBundleEvent("bundle_viewed", this.getBundleSelectionData());
    };

    if (!("IntersectionObserver" in window)) {
      window.setTimeout(trackViewed, 0);
      return;
    }

    this.bundleViewObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          trackViewed();
        }
      },
      { threshold: 0.25 },
    );
    this.bundleViewObserver.observe(this);
  }

  getBundleContext() {
    return {
      bundle_source: "pdp",
      parent_product_id: this.dataset.parentProductId || "",
      parent_product_handle: this.dataset.parentProductHandle || "",
      parent_product_title: this.dataset.parentProductTitle || "",
      currency: this.dataset.currency || window.Shopify?.currency?.active || "",
    };
  }

  centsToCurrencyValue(cents) {
    const amount = Number(cents);
    if (!Number.isFinite(amount)) return 0;
    return Number((amount / 100).toFixed(2));
  }

  getBundleItemData(item) {
    if (!item) return {};

    const priceCents = parseInt(item.dataset.priceCents, 10);

    return {
      product_id: item.dataset.productId || "",
      product_handle: item.dataset.productHandle || "",
      product_title: item.dataset.productTitle || "",
      product_vendor: item.dataset.productVendor || "",
      variant_id: item.dataset.variantId || "",
      sku: item.dataset.sku || "",
      price: this.centsToCurrencyValue(priceCents),
      price_cents: Number.isNaN(priceCents) ? 0 : priceCents,
      item_type:
        item.dataset.currentProduct === "true"
          ? "parent_product"
          : "cross_sell",
    };
  }

  getSelectedBundleItems() {
    return Array.from(
      this.querySelectorAll(".product-cross-sells__item.selected"),
    );
  }

  getBundleSelectionData(items = this.getSelectedBundleItems()) {
    const selectedItems = Array.from(items).map((item) =>
      this.getBundleItemData(item),
    );
    const bundleValueCents = selectedItems.reduce(
      (total, item) => total + (Number(item.price_cents) || 0),
      0,
    );

    return {
      selected_item_count: selectedItems.length,
      selected_variant_ids: selectedItems
        .map((item) => item.variant_id)
        .filter(Boolean),
      selected_skus: selectedItems.map((item) => item.sku).filter(Boolean),
      bundle_value: this.centsToCurrencyValue(bundleValueCents),
      bundle_value_cents: bundleValueCents,
      items: selectedItems,
    };
  }

  pushBundleEvent(eventName, payload = {}) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: eventName,
        ...this.getBundleContext(),
        ...payload,
      });
    } catch (error) {
      console.warn("Bundle tracking failed", error);
    }
  }

  syncCrossSellVariantItem(select, options = {}) {
    if (!select) return;

    const row = select.closest(".product-cross-sells__item");
    const option = select.selectedOptions?.[0];
    if (!row || !option) return;

    if (options.selectRow) row.classList.add("selected");

    const variantId = option.value;
    const priceCents = Number(option.dataset.priceCents);
    if (variantId) row.dataset.variantId = variantId;
    if (!Number.isNaN(priceCents)) row.dataset.priceCents = String(priceCents);
    row.dataset.sku = option.dataset.sku || "";

    const img = row.querySelector(".product-cross-sells__item-image");
    if (img && option.dataset.image) {
      img.src = option.dataset.image;
      img.removeAttribute("srcset");
    }

    const modalId = row
      .querySelector("modal-opener[data-modal]")
      ?.getAttribute("data-modal");
    if (modalId) {
      const modal = document.querySelector(modalId);
      const btn = modal?.querySelector(
        ".product-cross-sell-modal__add-to-cart-button",
      );
      if (btn && variantId) btn.dataset.variantId = variantId;
    }

    const priceEl = row.querySelector(".product-cross-sells__item-price");
    if (priceEl && !Number.isNaN(priceCents)) {
      priceEl.innerHTML = `+${this.formatMoney(priceCents, this.dataset.moneyFormat)}`;
    }

    this.updateTotals();
    this.updateStoreAvailability(row);
  }

  onProductVariantChange(event) {
    const t = event.target;
    const productSection = this.closest("section");
    if (!productSection || !productSection.contains(t)) return;

    const fromPicker = t.closest?.("variant-selects, variant-radios");
    const fromVariantId =
      t.matches?.('input[name="id"]') &&
      t.closest?.(`#product-form-${this.dataset.productSectionId}`);

    if (!fromPicker && !fromVariantId) return;

    queueMicrotask(() => this.syncCurrentProductBundleItem());
  }

  syncCurrentProductBundleItem() {
    const row = this.querySelector('[data-current-product="true"]');
    if (!row) return;

    const productSection = this.closest("section");
    const sectionId = this.dataset.productSectionId;
    if (!productSection || !sectionId) return;

    const picker = productSection.querySelector(
      `variant-selects[data-section="${sectionId}"], variant-radios[data-section="${sectionId}"]`,
    );
    if (!picker || typeof picker.getVariantData !== "function") return;

    const variants = picker.getVariantData();
    if (!variants?.length) return;

    const formInput = productSection.querySelector(
      `#product-form-${sectionId} input[name="id"]`,
    );
    const idRaw = formInput?.value || picker.currentVariant?.id;
    if (idRaw == null || idRaw === "") return;

    const vid = Number(idRaw);
    const v = variants.find((x) => Number(x.id) === vid);
    if (!v) return;

    const priceCents = Number(v.price);
    if (!Number.isNaN(priceCents)) {
      row.dataset.variantId = String(v.id);
      row.dataset.priceCents = String(priceCents);
    }
    row.dataset.sku = v.sku || "";

    const titleEl = row.querySelector(".product-cross-sells__item-title");
    if (titleEl) {
      const nextTitle = productSection
        .querySelector("#product__title")
        ?.textContent?.trim();
      if (nextTitle) titleEl.textContent = nextTitle;
    }

    const optionsEl = row.querySelector("[data-current-product-options]");
    if (optionsEl) {
      const nextOptions = v.public_title || v.title;
      const hasOptions = Boolean(
        nextOptions && nextOptions !== "Default Title",
      );
      optionsEl.textContent = hasOptions ? nextOptions : "";
      optionsEl.classList.toggle("hidden", !hasOptions);
    }

    const img = row.querySelector(".product-cross-sells__item-image");
    if (img) {
      let src =
        v.featured_image?.src ||
        v.featured_media?.preview_image?.src ||
        this.dataset.fallbackImage;
      if (src) {
        if (src.startsWith("//")) src = `https:${src}`;
        const join = src.includes("?") ? "&" : "?";
        img.src = `${src}${join}width=128`;
        img.removeAttribute("srcset");
      }
    }

    const priceEl = row.querySelector(".product-cross-sells__item-price");
    if (priceEl && !Number.isNaN(priceCents)) {
      const format = this.dataset.moneyFormat;
      const main = this.formatMoney(priceCents, format);
      const compareAt =
        v.compare_at_price != null ? Number(v.compare_at_price) : NaN;
      let html = main;
      if (!Number.isNaN(compareAt) && compareAt > priceCents) {
        const compareStr = this.formatMoney(compareAt, format);
        const savingsCents = compareAt - priceCents;
        const savingsStr = this.formatMoney(savingsCents, format);
        const saveTpl = this.dataset.saveTemplate;
        const saveText = saveTpl
          ? saveTpl.replace(/__SAVINGS__/g, savingsStr)
          : `Save ${savingsStr}`;
        html += `<span class="product-cross-sells__item-price-compare-at-price">-${compareStr}</span>`;
        html += `<span class="product-cross-sells__item-price-savings">${saveText}</span>`;
      }
      priceEl.innerHTML = html;
    }

    this.updateTotals();
    this.updateStoreAvailability(row);
  }

  onFavoriteStoreUpdated() {
    this.updateAllStoreAvailability();
  }

  updateAllStoreAvailability() {
    this.querySelectorAll(".product-cross-sells__item").forEach((item) => {
      this.updateStoreAvailability(item);
    });
  }

  updateStoreAvailability(item) {
    const message = item?.querySelector("[data-cross-sell-store-availability]");
    if (!message) return;

    const selectedStore = localStorage.getItem("storeSelected");
    const storeId = localStorage.getItem("storeSelectedId");
    const sku = item.dataset.sku;

    if (!sku) {
      delete item.dataset.storeAvailabilityRequest;
      message.classList.add("hidden");
      message.replaceChildren();
      return;
    }

    if (!storeId || !selectedStore) {
      delete item.dataset.storeAvailabilityRequest;
      const link = document.createElement("a");
      link.href = "/pages/store-locator";
      link.className = "store-locator-opener";
      link.textContent = "Favorite a Store to See Pickup Availability";
      link.addEventListener("click", (event) => event.stopPropagation());
      link.addEventListener("keydown", (event) => event.stopPropagation());
      message.replaceChildren(link);
      message.classList.remove("hidden");
      return;
    }

    const requestKey = `${storeId}:${sku}`;
    item.dataset.storeAvailabilityRequest = requestKey;
    message.classList.add("hidden");

    if (!this.availabilityRequests.has(requestKey)) {
      const requestUrl = new URL(
        "https://wcsp-shopify.azurewebsites.net/product-availability",
      );
      requestUrl.searchParams.set("item", sku);
      requestUrl.searchParams.set("store", storeId);

      this.availabilityRequests.set(
        requestKey,
        fetch(requestUrl)
          .then((response) => {
            if (!response.ok) throw new Error("Pickup availability failed");
            return response.json();
          })
          .then((response) => Number(response.res?.[0]?.quantity) > 0),
      );
    }

    this.availabilityRequests
      .get(requestKey)
      .then((isAvailable) => {
        if (item.dataset.storeAvailabilityRequest !== requestKey) return;

        message.textContent = isAvailable
          ? `Available for pickup at ${selectedStore}`
          : `Call ${selectedStore} for pickup availability`;
        message.classList.remove("hidden");
      })
      .catch(() => {
        if (item.dataset.storeAvailabilityRequest !== requestKey) return;

        this.availabilityRequests.delete(requestKey);
        message.textContent = `Call ${selectedStore} for pickup availability`;
        message.classList.remove("hidden");
      });
  }

  onClickItem(event) {
    if (event.target.closest(".product-popup-modal__opener")) return;

    const item = event.target.closest(".product-cross-sells__item");
    if (!item) return;

    const wasSelected = item.classList.contains("selected");
    item.classList.toggle("selected");
    this.updateTotals();

    this.pushBundleEvent(
      wasSelected ? "bundle_item_deselected" : "bundle_item_selected",
      {
        item: this.getBundleItemData(item),
        ...this.getBundleSelectionData(),
      },
    );
  }

  onKeyDownItem(event) {
    if (event.repeat) return;
    if (event.target.closest(".product-popup-modal__opener")) return;

    const item = event.target.closest(".product-cross-sells__item");
    if (!item) return;
    if (event.key !== "Enter" && event.key !== " ") return;

    event.preventDefault();
    const wasSelected = item.classList.contains("selected");
    item.classList.toggle("selected");
    this.updateTotals();

    this.pushBundleEvent(
      wasSelected ? "bundle_item_deselected" : "bundle_item_selected",
      {
        item: this.getBundleItemData(item),
        ...this.getBundleSelectionData(),
      },
    );
  }

  syncItemAccessibility() {
    this.querySelectorAll(".product-cross-sells__item").forEach((el) => {
      el.setAttribute(
        "aria-checked",
        el.classList.contains("selected") ? "true" : "false",
      );
    });
  }

  groupIntegerWithSeparator(integer, sep) {
    const n = Math.trunc(integer);
    const sign = n < 0 ? "-" : "";
    const digits = String(Math.abs(n));
    const parts = [];
    for (let i = digits.length; i > 0; i -= 3) {
      parts.unshift(digits.slice(Math.max(0, i - 3), i));
    }
    return sign + parts.join(sep);
  }

  formatMoney(cents, format) {
    if (typeof cents !== "number" || Number.isNaN(cents)) {
      return "";
    }
    if (!format || typeof format !== "string") {
      const v = cents / 100;
      return v.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }

    const absCents = Math.abs(cents);
    const negative = cents < 0;
    const sign = negative ? "-" : "";
    const intPart = Math.floor(absCents / 100);
    const frac2 = String(absCents % 100).padStart(2, "0");
    const roundedInt = Math.round(cents / 100);

    const amount = `${sign}${this.groupIntegerWithSeparator(intPart, ",")}.${frac2}`;
    const amountNoDecimals = `${sign}${this.groupIntegerWithSeparator(roundedInt, ",")}`;
    const amountWithCommaSeparator = `${sign}${this.groupIntegerWithSeparator(intPart, ".")},${frac2}`;
    const amountNoDecimalsWithCommaSeparator = `${sign}${this.groupIntegerWithSeparator(roundedInt, ".")}`;
    const amountWithApostropheSeparator = `${sign}${this.groupIntegerWithSeparator(intPart, "'")}.${frac2}`;
    const amountWithSpaceSeparator = `${sign}${this.groupIntegerWithSeparator(intPart, " ")},${frac2}`;
    const amountNoDecimalsWithSpaceSeparator = `${sign}${this.groupIntegerWithSeparator(roundedInt, " ")}`;
    const amountWithPeriodAndSpaceSeparator = `${sign}${this.groupIntegerWithSeparator(intPart, " ")}.${frac2}`;

    const tokenPattern = (name) =>
      new RegExp(`\\{\\{\\s*${name}\\s*\\}\\}`, "g");

    let result = format;
    const replacements = [
      [
        "amount_no_decimals_with_comma_separator",
        amountNoDecimalsWithCommaSeparator,
      ],
      [
        "amount_no_decimals_with_space_separator",
        amountNoDecimalsWithSpaceSeparator,
      ],
      ["amount_no_decimals", amountNoDecimals],
      ["amount_with_comma_separator", amountWithCommaSeparator],
      ["amount_with_apostrophe_separator", amountWithApostropheSeparator],
      [
        "amount_with_period_and_space_separator",
        amountWithPeriodAndSpaceSeparator,
      ],
      ["amount_with_space_separator", amountWithSpaceSeparator],
      ["amount", amount],
    ];

    for (const [name, value] of replacements) {
      result = result.replace(tokenPattern(name), value);
    }
    return result;
  }

  renderAddAllDefaultLabel() {
    const textEl = this.querySelector(
      ".product-cross-sells__add-all-to-cart-button .product-cross-sells__add-all-button-text",
    );
    if (!textEl) return;
    const selected = this.querySelectorAll(
      ".product-cross-sells__item.selected",
    );
    const tpl = this.dataset.addAllTemplate;
    if (tpl) {
      textEl.innerHTML = tpl.replace(/__N__/g, String(selected.length));
      return;
    }
    textEl.innerHTML = `Add All (<span class="product-cross-sells__total-quantity">${selected.length}</span>) to Cart`;
  }

  updateTotals() {
    const selected = this.querySelectorAll(
      ".product-cross-sells__item.selected",
    );
    let totalCents = 0;
    selected.forEach((el) => {
      const cents = parseInt(el.dataset.priceCents, 10);
      if (!Number.isNaN(cents)) totalCents += cents;
    });

    const format = this.dataset.moneyFormat;
    const totalEl = this.querySelector(
      ".product-cross-sells__total-price-value",
    );
    if (totalEl) totalEl.innerHTML = this.formatMoney(totalCents, format);

    const countEl = this.querySelector(".product-cross-sells__total-quantity");
    if (countEl) {
      countEl.textContent = String(selected.length);
    } else {
      this.renderAddAllDefaultLabel();
    }

    this.syncItemAccessibility();
    this.syncAddAllToCartButtonDisabledState();
  }

  syncAddAllToCartButtonDisabledState() {
    const btn = this.querySelector(
      ".product-cross-sells__add-all-to-cart-button",
    );
    if (!btn || btn.classList.contains("loading")) return;
    const n = this.querySelectorAll(
      ".product-cross-sells__item.selected",
    ).length;
    btn.disabled = n === 0;
  }

  setAddAllLoading(btn, loading) {
    const textEl = btn?.querySelector(
      ".product-cross-sells__add-all-button-text",
    );
    const spinnerEl = btn?.querySelector(".loading-overlay__spinner");
    if (loading) {
      btn.disabled = true;
      btn.classList.add("loading");
      if (textEl) textEl.setAttribute("aria-hidden", "true");
      if (spinnerEl) spinnerEl.classList.remove("hidden");
    } else {
      btn.classList.remove("loading");
      if (textEl) textEl.removeAttribute("aria-hidden");
      if (spinnerEl) spinnerEl.classList.add("hidden");
      this.syncAddAllToCartButtonDisabledState();
    }
  }

  async refreshCartSections() {
    const sectionIds = ["cart-drawer", "header"];
    const res = await fetch(
      `/?sections=${encodeURIComponent(sectionIds.join(","))}`,
      {
        credentials: "same-origin",
      },
    );
    if (!res.ok) throw new Error("Failed to fetch cart sections");
    const sections = await res.json();
    const parseHTML = (html) =>
      new DOMParser().parseFromString(html, "text/html");
    // Header must update before cart-drawer: CartDrawer binds click handlers to
    // #cart-icon-bubble in its constructor; replacing the bubble after the drawer
    // swaps those listeners onto a detached node.
    if (sections["header"]) {
      const freshBubble = parseHTML(sections["header"]).querySelector(
        "#cart-icon-bubble",
      );
      const currentBubble = document.querySelector("#cart-icon-bubble");
      if (freshBubble && currentBubble) currentBubble.replaceWith(freshBubble);
    }
    if (sections["cart-drawer"]) {
      const currentDrawer = document.querySelector("cart-drawer");
      if (currentDrawer) {
        const freshDrawer = parseHTML(sections["cart-drawer"]).querySelector(
          "cart-drawer",
        );
        if (freshDrawer) currentDrawer.replaceWith(freshDrawer);
      }
    }
  }

  openCartDrawer() {
    const drawerEl = document.querySelector("cart-drawer");
    if (!drawerEl) return;
    if (typeof drawerEl.open === "function") {
      drawerEl.open();
      return;
    }
    drawerEl.style.display = "block";
    drawerEl.style.visibility = "visible";
    drawerEl.classList.add("active");
  }

  onModalOpenedResetCrossSellButtons() {
    const label = this.dataset.addProduct || "Add Product";
    this.querySelectorAll("modal-opener[data-modal]").forEach((opener) => {
      const target = opener.getAttribute("data-modal");
      if (!target) return;
      const modal = document.querySelector(target);
      if (!modal?.matches("modal-dialog.product-popup-modal--cross-sell[open]"))
        return;
      const btn = modal.querySelector(
        ".product-cross-sell-modal__add-to-cart-button",
      );
      if (!btn) return;
      btn.disabled = false;
      const textEl = btn.querySelector(
        ".product-cross-sell-modal__add-to-cart-button-text",
      );
      if (textEl) textEl.textContent = label;
      else btn.textContent = label;
    });
  }

  async onClickModalAddToCart(event) {
    const btn = event.target.closest(
      ".product-cross-sell-modal__add-to-cart-button",
    );
    if (!btn || btn.disabled) return;

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    const modal = btn.closest("modal-dialog");
    if (!modal || typeof modal.hide !== "function") return;

    event.preventDefault();
    event.stopPropagation();

    const textEl = btn.querySelector(
      ".product-cross-sell-modal__add-to-cart-button-text",
    );
    const originalText = textEl ? textEl.textContent : btn.textContent;
    const addingLabel = this.dataset.adding || "Adding...";
    if (textEl) textEl.textContent = addingLabel;
    btn.disabled = true;

    const messageEl = modal.querySelector(
      ".product-cross-sell-modal__add-error",
    );
    if (messageEl) messageEl.hidden = true;

    const root = window.Shopify?.routes?.root ?? "/";
    const url = root + (root.endsWith("/") ? "" : "/") + "cart/add.js";

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          items: [{ id: parseInt(variantId, 10), quantity: 1 }],
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          data.description ||
            data.message ||
            this.dataset.couldNotAdd ||
            "Could not add to cart",
        );
      }

      if (messageEl) {
        messageEl.textContent = "";
        messageEl.hidden = true;
      }
      modal.hide();
      btn.disabled = false;
      if (textEl) textEl.textContent = originalText;
      else btn.textContent = originalText;
      await this.refreshCartSections();
      this.openCartDrawer();
      window.searchspringTracking?.scheduleCartViewSync?.();
    } catch (err) {
      btn.disabled = false;
      if (textEl) textEl.textContent = originalText;
      else btn.textContent = originalText;
      if (messageEl) {
        messageEl.textContent =
          err?.message ||
          (typeof err === "string" ? err : "") ||
          this.dataset.genericError ||
          "Something went wrong";
        messageEl.hidden = false;
      }
    }
  }

  async onClickAddAll(event) {
    const btn = event.target.closest(
      ".product-cross-sells__add-all-to-cart-button",
    );
    if (!btn || btn.disabled) return;

    const selected = this.querySelectorAll(
      ".product-cross-sells__item.selected",
    );
    const items = [];
    selected.forEach((el) => {
      const id = el.dataset.variantId;
      if (id) items.push({ id: parseInt(id, 10), quantity: 1 });
    });
    if (items.length === 0) return;

    const bundleSelectionData = this.getBundleSelectionData(selected);
    this.pushBundleEvent("bundle_add_all_clicked", bundleSelectionData);

    const root = window.Shopify?.routes?.root ?? "/";
    const url = root + (root.endsWith("/") ? "" : "/") + "cart/add.js";

    const labelEl = btn.querySelector(
      ".product-cross-sells__add-all-button-text",
    );
    if (
      labelEl &&
      !labelEl.querySelector(".product-cross-sells__total-quantity")
    ) {
      this.renderAddAllDefaultLabel();
    }

    this.setAddAllLoading(btn, true);
    const messageEl = this.querySelector(".product-cross-sells__add-all-error");
    if (messageEl) messageEl.hidden = true;

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ items }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const message =
          data.description ||
          data.message ||
          this.dataset.couldNotAddItems ||
          "Could not add items to cart";
        throw new Error(message);
      }

      this.setAddAllLoading(btn, false);
      const textEl = btn.querySelector(
        ".product-cross-sells__add-all-button-text",
      );
      if (textEl) textEl.textContent = this.dataset.added || "Added";
      this.pushBundleEvent("bundle_add_all_success", {
        ...bundleSelectionData,
        cart_items_added_count: items.length,
      });
      window.searchspringTracking?.scheduleCartViewSync?.();

      await this.refreshCartSections();
      this.openCartDrawer();
    } catch (err) {
      this.setAddAllLoading(btn, false);
      this.renderAddAllDefaultLabel();
      this.pushBundleEvent("bundle_add_all_error", {
        ...bundleSelectionData,
        cart_items_attempted_count: items.length,
        error_message:
          err?.message || this.dataset.genericError || "Something went wrong",
      });
      if (messageEl) {
        messageEl.textContent =
          err.message || this.dataset.genericError || "Something went wrong";
        messageEl.hidden = false;
      }
    }
  }

  onClickToggleShowMore(event) {
    const btn = event.currentTarget;
    if (!btn?.classList.contains("product-cross-sells__show-more-button"))
      return;

    const list = this.querySelector(".product-cross-sells__list");
    if (!list) return;

    const expanded = list.classList.toggle("product-cross-sells--expanded");
    const moreLabel = this.dataset.showMoreLabel || "Show More";
    const lessLabel = this.dataset.showLessLabel || "Show Less";

    btn.textContent = expanded ? lessLabel : moreLabel;
    btn.setAttribute("aria-expanded", expanded ? "true" : "false");

    if (expanded) {
      this.pushBundleEvent(
        "bundle_show_more_clicked",
        this.getBundleSelectionData(),
      );
    }
  }
}

if (!customElements.get("product-cross-sells")) {
  customElements.define("product-cross-sells", ProductCrossSells);
}
